import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Row } from "../types";
import { api, ApiError } from "../api";
import type { ItemMaterial } from "../api";
import { IconClose, IconTrash, IconCheck, IconDownload, IconSend, IconCamera, IconBox } from "../icons";
import { usePodeExcluir } from "../permissoes";
import CameraScanner from "./CameraScanner";
import { chaveDoCodigo } from "../leitor-camera";

/**
 * CONFERÊNCIA DA REMESSA (bipagem).
 *
 * Tela de bancada: a pessoa está de luva, com a caixa aberta na frente e o
 * leitor na mão. Tudo aqui é feito pensando nisso:
 *
 *  - O CAMPO NUNCA PERDE O FOCO. O leitor USB se comporta como teclado e manda
 *    Enter no fim. Se o foco escapar (um clique em qualquer lugar da tela), a
 *    próxima leitura some — e a pessoa só descobre quando a conta não fecha.
 *    Por isso o campo se refoca sozinho depois de cada bipagem e ao clicar em
 *    qualquer canto do modal.
 *  - DUPLICADO É AVISO AMARELO, NÃO ERRO. Ler o mesmo frasco duas vezes é
 *    rotina do leitor. Piscar vermelho no meio de 40 frascos faria a pessoa
 *    parar para ler a mensagem toda vez.
 *  - O CONTADOR É O QUE IMPORTA. Fica grande e permanente: previsto × conferido
 *    e a diferença. É esse número que decide se a caixa pode fechar.
 *  - O ÚLTIMO BIPADO aparece em destaque, porque é a confirmação que a pessoa
 *    procura sem tirar os olhos da caixa.
 */
interface Props {
  registro: Row;
  onCancel: () => void;
  onSaved: () => void;
}

type Aviso = { tipo: "ok" | "atencao" | "erro"; texto: string } | null;

export default function MaterialConferencia({ registro, onCancel, onSaved }: Props) {
  const podeExcluir = usePodeExcluir();
  const id = Number(registro.id);

  const [remessa, setRemessa] = useState<Row>(registro);
  const [itens, setItens] = useState<ItemMaterial[]>([]);
  const [previsto, setPrevisto] = useState(Number(registro.quantidade_prevista) || 0);
  const [codigo, setCodigo] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<Aviso>(null);
  const [ultimo, setUltimo] = useState<string | null>(null);
  /** Fica true quando o servidor pediu confirmação para fechar com divergência. */
  const [confirmarFechar, setConfirmarFechar] = useState(false);
  const [rastreio, setRastreio] = useState(String(registro.correios_rastreio ?? ""));
  /** Painel da câmera aberto (leitura pelo celular, em vez do leitor de mão). */
  const [camAberta, setCamAberta] = useState(false);

  const campoRef = useRef<HTMLInputElement>(null);
  const enviada = String(remessa.status) === "Enviado";
  const fechada = String(remessa.status) === "Separado" || enviada;

  /**
   * Devolve o foco ao campo do leitor — é o estado normal desta tela.
   *
   * Com a CÂMERA aberta, não: no celular, focar o campo abre o teclado por cima
   * do vídeo e a pessoa perde a mira. O foco volta sozinho quando a câmera
   * fecha (é por isso que `fecharCamera` chama `focar()` de propósito).
   */
  const focar = useCallback(() => {
    if (!enviada && !camAberta) setTimeout(() => campoRef.current?.focus(), 0);
  }, [enviada, camAberta]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.material.conferencia(id);
      setRemessa(r.remessa);
      setItens(r.itens);
      setPrevisto(r.previsto);
      setRastreio(String(r.remessa.correios_rastreio ?? ""));
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao carregar a remessa." });
    } finally {
      setCarregando(false);
      focar();
    }
  }, [id, focar]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * REGISTRA UM CÓDIGO — o caminho único, venha do leitor de mão, da digitação
   * ou da câmera.
   *
   * Existe separado do formulário justamente para a câmera não ter uma segunda
   * regra de duplicado, de erro e de "último conferido": tudo o que vale para o
   * leitor de mão vale igual para a câmera, de graça.
   *
   * O item novo entra na lista SEM recarregar a remessa inteira: no celular,
   * uma recarga completa a cada frasco fazia a leitura contínua engasgar.
   * A recarga continua acontecendo onde importa (abrir a tela, fechar a câmera,
   * remover item, fechar e enviar a remessa).
   */
  const registrarCodigo = useCallback(
    async (bruto: string, origem: "leitor" | "camera"): Promise<"novo" | "duplicado" | "erro"> => {
      const valor = bruto.trim();
      if (!valor) return "erro";
      try {
        const r = await api.material.bipar(id, valor, { origem });
        if (r.duplicado) {
          setAviso({ tipo: "atencao", texto: r.mensagem ?? `"${valor}" já foi conferido.` });
          return "duplicado";
        }
        setUltimo(valor);
        setAviso({
          tipo: "ok",
          texto: r.completo
            ? `${valor} conferido — a conta fechou (${r.conferido} de ${r.previsto}).`
            : `${valor} conferido.`,
        });
        if (r.item) setItens((atuais) => [r.item as ItemMaterial, ...atuais]);
        else await carregar();
        return "novo";
      } catch (err) {
        setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao bipar." });
        return "erro";
      }
    },
    [id, carregar],
  );

  async function bipar(e: React.FormEvent) {
    e.preventDefault();
    const valor = codigo.trim();
    if (!valor || ocupado) return;
    setOcupado(true);
    try {
      const resultado = await registrarCodigo(valor, "leitor");
      if (resultado !== "erro") setCodigo("");
    } finally {
      setOcupado(false);
      focar();
    }
  }

  /** Códigos já conferidos, na mesma forma que o banco compara (LOWER(TRIM)). */
  const jaConferidos = useMemo(
    () => new Set(itens.map((i) => chaveDoCodigo(i.codigo))),
    [itens],
  );

  /** Fecha o painel da câmera: recarrega a remessa e devolve o foco ao leitor. */
  const fecharCamera = useCallback(async () => {
    setCamAberta(false);
    await carregar();
    setTimeout(() => campoRef.current?.focus(), 0);
  }, [carregar]);

  async function remover(item: ItemMaterial) {
    if (!confirm(`Tirar "${item.codigo}" da conferência?`)) return;
    try {
      await api.material.removerItem(id, item.id);
      setAviso({ tipo: "atencao", texto: `"${item.codigo}" removido da conferência.` });
      await carregar();
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao remover." });
    }
  }

  async function fechar(forcar = false) {
    setOcupado(true);
    try {
      const r = await api.material.fechar(id, forcar);
      if (r?.divergencia) {
        // Não é erro: é a última chance de perceber a diferença antes de fechar.
        setConfirmarFechar(true);
        setAviso({ tipo: "atencao", texto: r.mensagem });
        return;
      }
      setConfirmarFechar(false);
      setAviso({ tipo: "ok", texto: r?.mensagem ?? "Remessa fechada." });
      await carregar();
      onSaved();
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao fechar." });
    } finally {
      setOcupado(false);
    }
  }

  async function enviar() {
    setOcupado(true);
    try {
      const r = await api.material.enviar(id, rastreio.trim() || undefined);
      setAviso({ tipo: "ok", texto: r?.mensagem ?? "Remessa marcada como enviada." });
      await carregar();
      onSaved();
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao enviar." });
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Abre um dos três impressos da remessa numa aba nova.
   *
   * Por que não é um link: o documento vem de uma rota que exige login, e o
   * navegador não manda cabeçalho customizado numa navegação (`<a href>`,
   * `window.open` de URL). Com link direto, a aba nova abria com o JSON de
   * "faça login" no lugar da declaração dos Correios.
   *
   * A janela é aberta ANTES do fetch de propósito: aberta depois, o navegador a
   * trata como pop-up não pedido pela pessoa e bloqueia.
   */
  async function abrirDocumento(doc: "declaracao" | "etiqueta" | "conferencia", rotulo: string) {
    const janela = window.open("", "_blank");
    if (!janela) {
      setAviso({
        tipo: "erro",
        texto: "O navegador bloqueou a janela. Libere os pop-ups para este site e tente de novo.",
      });
      return;
    }
    janela.document.write(`<p style="font:14px system-ui;padding:24px">Gerando ${rotulo}...</p>`);
    try {
      const html = await api.material.documento(id, doc);
      janela.document.open();
      janela.document.write(html);
      janela.document.close();
      janela.focus();
    } catch (err) {
      janela.close();
      setAviso({
        tipo: "erro",
        texto: err instanceof ApiError ? err.message : `Não foi possível gerar ${rotulo}.`,
      });
    }
  }

  const conferido = itens.length;
  const diferenca = conferido - previsto;
  const toneContador = previsto === 0 ? "" : diferenca === 0 ? " ok" : " alerta";

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div
        className="modal-card modal-wide conf-modal"
        onMouseDown={(e) => e.stopPropagation()}
        // Clicar em qualquer canto devolve o foco ao leitor: sem isso, um
        // clique distraído faz a próxima leitura sumir sem ninguém notar.
        onClick={focar}
      >
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            <span className="rf-head-icon">
              <IconBox size={20} />
            </span>
            <div>
              <h3>Conferência da remessa</h3>
              <p className="rf-head-sub">
                OS {String(remessa.os_numero ?? "—")} · {String(remessa.cliente ?? "cliente não informado")}
                {remessa.equipamento ? ` · ${remessa.equipamento}` : ""}
              </p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onCancel} title="Fechar (Esc)">
            <IconClose size={18} />
          </button>
        </div>

        <div className="conf-corpo">
          {/* Contador: é o número que decide se a caixa fecha. */}
          <div className={`conf-contador${toneContador}`}>
            <div>
              <span className="conf-num">{conferido}</span>
              <span className="conf-rot">conferidos</span>
            </div>
            <div className="conf-sep">de</div>
            <div>
              <span className="conf-num">{previsto || "—"}</span>
              <span className="conf-rot">previstos</span>
            </div>
            {previsto > 0 && (
              <div className="conf-dif">
                {diferenca === 0
                  ? "a conta fechou"
                  : diferenca > 0
                    ? `${diferenca} a mais`
                    : `${Math.abs(diferenca)} faltando`}
              </div>
            )}
          </div>

          {enviada ? (
            <div className="conf-aviso atencao">
              Remessa já enviada{remessa.correios_rastreio ? ` (rastreio ${remessa.correios_rastreio})` : ""} —
              a conferência está travada.
            </div>
          ) : (
            <form className="conf-leitor" onSubmit={bipar}>
              <label>
                <span>Bipe o código (ou digite e pressione Enter)</span>
                <input
                  ref={campoRef}
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  placeholder="aguardando o leitor…"
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  disabled={ocupado}
                />
              </label>
              <button type="submit" className="btn-primary" disabled={ocupado || !codigo.trim()}>
                <IconCheck size={16} /> Conferir
              </button>
              {/*
                type="button" é obrigatório: dentro de um <form>, o padrão de um
                botão é ENVIAR — sem isso, abrir a câmera dispararia uma bipagem
                em branco.
              */}
              {!camAberta && (
                <button type="button" className="btn-secondary" onClick={() => setCamAberta(true)}>
                  <IconCamera size={16} /> Ler pela câmera
                </button>
              )}
            </form>
          )}

          {/*
            O painel da câmera fica DENTRO do card, nunca por cima: o fundo
            escuro do modal fecha a conferência ao primeiro toque.
          */}
          {camAberta && !enviada && (
            <CameraScanner
              jaConferidos={jaConferidos}
              onCodigo={(texto) => registrarCodigo(texto, "camera")}
              onFechar={() => void fecharCamera()}
              onDigitar={() => void fecharCamera()}
            />
          )}

          {aviso && <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>}
          {ultimo && !aviso && <div className="conf-aviso ok">Último conferido: {ultimo}</div>}

          {/* Lista do que já entrou — o mais recente em cima, que é o que se confere. */}
          <div className="conf-lista">
            {carregando ? (
              <div className="empty">Carregando…</div>
            ) : itens.length === 0 ? (
              <div className="empty">Nada conferido ainda — bipe o primeiro item.</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Código</th>
                    <th>Descrição</th>
                    <th>Tipo</th>
                    <th>Bipado por</th>
                    <th className="col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((i, n) => (
                    <tr key={i.id} className={i.codigo === ultimo ? "conf-ultimo" : undefined}>
                      <td>{itens.length - n}</td>
                      <td><strong>{i.codigo}</strong></td>
                      <td>{i.descricao ?? "—"}</td>
                      <td>{i.tipo ?? "—"}</td>
                      <td>
                        {i.bipado_por ?? "—"}
                        {/* Marca só o que veio da câmera: é o que se quer conferir quando um código sai errado. */}
                        {i.origem === "camera" && <span className="conf-tag-camera">câmera</span>}
                      </td>
                      <td className="col-actions">
                        {!fechada && podeExcluir && (
                          <button className="icon-btn icon-btn-danger" title="Tirar da conferência" onClick={() => remover(i)}>
                            <IconTrash size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Documentos: abrem em aba nova, prontos para Ctrl+P → salvar em PDF. */}
          <div className="conf-docs">
            <strong>Documentos da remessa</strong>
            <div className="conf-docs-botoes">
              {([
                ["declaracao", "Declaração de conteúdo"],
                ["etiqueta", "Etiqueta de endereçamento"],
                ["conferencia", "Lista de conferência"],
              ] as const).map(([doc, rotulo]) => (
                <button
                  key={doc}
                  type="button"
                  className="btn-secondary"
                  onClick={() => void abrirDocumento(doc, rotulo)}
                >
                  <IconDownload size={15} /> {rotulo}
                </button>
              ))}
            </div>
            <span className="muted">
              Abrem em nova aba já formatados — use Ctrl+P e “Salvar como PDF”.
            </span>
          </div>
        </div>

        <div className="modal-foot conf-foot">
          {!fechada && (
            <button
              className="btn-primary"
              disabled={ocupado || conferido === 0}
              onClick={() => fechar(confirmarFechar)}
            >
              <IconCheck size={16} />{" "}
              {confirmarFechar ? "Fechar mesmo com a diferença" : "Fechar conferência"}
            </button>
          )}
          {String(remessa.status) === "Separado" && (
            <>
              <input
                className="conf-rastreio"
                value={rastreio}
                onChange={(e) => setRastreio(e.target.value)}
                placeholder="código de rastreio (opcional)"
              />
              <button className="btn-primary" disabled={ocupado} onClick={enviar}>
                <IconSend size={16} /> Marcar como enviada
              </button>
            </>
          )}
          <button className="btn-secondary" onClick={onCancel}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
